import { format } from "date-fns";

import { Badge, BadgeProps, Column, Table } from "@fronterahealth/frontera-ui-components";

import { ReportTemplateType, useDeleteReportMutation } from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

export const STATUS_VALUES = [
  "Client Details Needed",
  "At Upload Files",
  "Complete",
  "At Report Page 1",
  "At Report Page 2",
  "At Conclusions",
  "At Recommendations",
  "Generating Report Page 1",
  "Generating Report Page 2",
  "Generating Conclusions",
  "Generating Recommendations",
  "Error on Report Page 1",
  "Error on Report Page 2",
  "Error on Conclusions",
  "Error on Recommendations",
] as const;
export type StatusType = (typeof STATUS_VALUES)[number];

const BadgeSettingFromStatus: { [key in StatusType]: BadgeProps["appearance"] } = {
  "Generating Report Page 1": "ai",
  "Generating Report Page 2": "ai",
  "Generating Conclusions": "ai",
  "Generating Recommendations": "ai",
  "At Report Page 1": "neutral",
  "At Report Page 2": "neutral",
  "At Conclusions": "neutral",
  "At Recommendations": "neutral",
  Complete: "success",
  "Error on Report Page 1": "error",
  "Error on Report Page 2": "error",
  "Error on Conclusions": "error",
  "Error on Recommendations": "error",
  "At Upload Files": "neutral",
  "Client Details Needed": "warn",
};

export interface Evaluation {
  id: string;
  clientName: string;
  reportTemplate: ReportTemplateType | null;
  dateCreated: Date;
  lastUpdated: Date;
  status: StatusType;
  pagesDone: number;
}

interface EvaluationsTableProps {
  data: (Evaluation | null)[];
  onRowClick: (report: Evaluation) => void;
  loading?: boolean;
  refetch: () => Promise<unknown>;
}

export const EvaluationsTable: React.FC<EvaluationsTableProps> = ({ data, onRowClick, loading, refetch }) => {
  const { roles } = useAdminData();
  const deleteReportMutation = useDeleteReportMutation({
    onSuccess: async () => {
      await refetch();
      notifySuccess("Report successfully deleted");
    },
    onError: (err) => {
      console.error("Failed to delete report", err);
      notifyError("Unable to delete report");
    },
  });

  const columns = [
    {
      dataIndex: "clientName",
      key: "clientName",
      title: "Client Name",
      render: (value: string) => <span className="ph-no-capture">{value}</span>,
    },
    {
      dataIndex: "reportTemplate" as keyof Evaluation,
      key: "reportTemplate",
      title: "Report Template",
      render: (value: ReportTemplateType) => value?.name || "No template",
    },
    {
      dataIndex: "dateCreated",
      key: "dateCreated",
      title: "Date Created",
      render: (value: Date) => (value ? format(value, "MM/dd/yyyy") : ""),
    },
    {
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      title: "Last Updated",
      render: (value: Date) => (value ? format(value, "MM/dd/yyyy") : ""),
    },
    {
      dataIndex: "status",
      key: "status",
      title: "Status",
      render: (value: string) => {
        return <Badge appearance={BadgeSettingFromStatus[value as StatusType]} text={value as StatusType} />;
      },
    },
  ];
  const isInternalUser = roles.includes("frontera-internal");
  const actions = [
    {
      label: "View",
      callback: (rowData: Evaluation) => onRowClick(rowData),
    },
    ...(isInternalUser
      ? [
          {
            label: "Delete",
            textColorClassName: "text-red-700",
            callback: (rowData: { id: string }) => {
              deleteReportMutation.mutate({ report: rowData.id });
            },
          },
        ]
      : []),
  ];

  return (
    <div className="w-full bg-white mt-8 rounded-lg">
      <Table
        dataTestId={"evaluations-table"}
        onRowClick={onRowClick}
        loading={loading}
        defaultItemsPerPage={10}
        showSearch
        inputClassName="w-96"
        showPagination
        itemsPerPageOptions={[5, 10, 20, 50]}
        actions={actions}
        data={(data.filter((evaluation) => evaluation !== null) as Evaluation[]).sort((evaluationA, evaluationB) =>
          evaluationB.lastUpdated > evaluationA.lastUpdated ? 1 : -1,
        )}
        columns={columns as Column<Evaluation>[]}
      />
    </div>
  );
};
